// biome-ignore-all lint/correctness/useExhaustiveDependencies: historic
import type BulkInput from '@components/BulkInput/BulkInput';
import { PopoverContext } from '@context/popover-context';
import useNode from '@hooks/use-node';
import { useContext, useEffect, useRef } from 'react';

type BulkInputHandle = React.ElementRef<typeof BulkInput>;

type UseCardPopoverInputFocusArgs = {
  onFocus?: (node: HTMLInputElement) => void;
  node?: HTMLInputElement;
};

export function useCardPopoverInputFocus(options?: UseCardPopoverInputFocusArgs) {
  const { show } = useContext(PopoverContext);
  const [inputNode, inputRef] = useNode<HTMLInputElement>();
  const currentNode = options?.node ? options.node : inputNode;
  useEffect(() => {
    if (show && currentNode) {
      const intersectionObserver = new IntersectionObserver((entries, observer) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setTimeout(() => {
            currentNode.focus();
            options?.onFocus && options.onFocus(currentNode);
          }, 300);
          observer.unobserve(entry.target);
        }
      });

      intersectionObserver.observe(currentNode);
    }
  }, [show, inputNode]);

  return { inputRef, inputNode };
}

export function useCardPopoverBulkInputFocus() {
  const bulkInputRef = useRef<BulkInputHandle>(null);
  const getLastNode = () => {
    if (bulkInputRef.current?.nodes && bulkInputRef.current?.nodes?.length > 0) {
      return bulkInputRef.current?.nodes[0];
    }
  };
  const { inputNode } = useCardPopoverInputFocus({ node: getLastNode() });
  return { inputNode, inputRef: bulkInputRef };
}
